import React from 'react';

import { useSubscription } from '../../../context/SubscriptionContext';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import FeeCategoriesSettings from '../../Settings/FeeCategoriesSettings';
import LateFeePolicySettings from '../../Settings/LateFeePolicySettings';
import CategoryTreeSettings from '../../Settings/categoryTree/CategoryTreeSettings';
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES } from '../../Settings/categoryTree/configs';
import ChartOfAccountsPanel from './ChartOfAccountsPanel';
import FiscalPeriodsPanel from './FiscalPeriodsPanel';
import AccountingPreferencesPanel from './AccountingPreferencesPanel';
import OpeningBalancesPanel from './OpeningBalancesPanel';

/** Setup item ids — also used by system Settings to redirect old deep links. */
export const FINANCE_SETUP_IDS = [
  'fee-categories', 'late-fee-policy', 'revenue-categories', 'expense-categories',
  'chart-of-accounts', 'opening-balances', 'fiscal-periods', 'accounting-preferences',
] as const;
export type FinanceSetupId = typeof FINANCE_SETUP_IDS[number];

export interface FinanceSetupItem {
  id: FinanceSetupId;
  group: string;
  title: string;
  description: string;
  render: () => React.ReactNode;
}

/**
 * Everything that configures money, in one list. The sidebar shows "Setup"
 * when this returns anything; the Setup page renders exactly these. Each
 * item keeps its own granular `settings_*` scope, so roles granted before
 * the move keep working unchanged.
 */
export const useFinanceSetupItems = (): FinanceSetupItem[] => {
  const { hasFeature } = useSubscription();
  const { canRead } = useModulePermissions();

  const all: Array<FinanceSetupItem & { visible: boolean }> = [
    {
      id: 'fee-categories',
      group: 'Fees',
      title: 'Fee categories',
      description: 'The fee heads that make up a fee plan and appear on challans: tuition, transport, exam and so on.',
      render: () => <FeeCategoriesSettings />,
      visible: hasFeature('fees') && canRead('settings_fee_categories'),
    },
    {
      id: 'late-fee-policy',
      group: 'Fees',
      title: 'Due dates & late fees',
      description: 'When challans fall due, and when and how much to charge once they are overdue: grace days, amount and cap.',
      render: () => <LateFeePolicySettings />,
      visible: hasFeature('fees') && canRead('settings_late_fee_policy'),
    },
    {
      id: 'revenue-categories',
      group: 'Income & expenses',
      title: 'Revenue categories',
      description: 'How money coming in outside fees is classified: donations, sales, rent and so on.',
      render: () => <CategoryTreeSettings config={INCOME_CATEGORIES} />,
      visible: hasFeature('expenses') && canRead('settings_revenue_categories'),
    },
    {
      id: 'expense-categories',
      group: 'Income & expenses',
      title: 'Expense categories',
      description: 'How money going out is classified: salaries, rent, utilities and so on.',
      render: () => <CategoryTreeSettings config={EXPENSE_CATEGORIES} />,
      visible: hasFeature('expenses') && canRead('settings_expense_categories'),
    },
    {
      id: 'chart-of-accounts',
      group: 'Accounting',
      title: 'Chart of accounts',
      description: 'Every account the books post to, with its balance. Add accounts under a group; switch on reserved ones when needed.',
      render: () => <ChartOfAccountsPanel />,
      visible: hasFeature('expenses') && canRead('finance_accounts'),
    },
    {
      id: 'opening-balances',
      group: 'Accounting',
      title: 'Opening balances',
      description: 'What each bank, cash and balance-sheet account held on the day the school started keeping its books here. Capital balances the rest.',
      render: () => <OpeningBalancesPanel />,
      visible: hasFeature('expenses') && canRead('finance_accounts'),
    },
    {
      id: 'fiscal-periods',
      group: 'Accounting',
      title: 'Fiscal periods',
      description: 'The July–June year month by month. Close a month when its books are done; lock it when they are final.',
      render: () => <FiscalPeriodsPanel />,
      visible: hasFeature('expenses') && canRead('finance_periods'),
    },
    {
      id: 'accounting-preferences',
      group: 'Accounting',
      title: 'Preferences',
      description: "The school's legal form, where each payment method lands, and whether a second person approves money decisions.",
      render: () => <AccountingPreferencesPanel />,
      visible: hasFeature('expenses') && canRead('finance_accounts'),
    },
  ];

  return all.filter(i => i.visible).map(({ visible, ...item }) => item);
};
